(function () {
  'use strict';

  const { subEvents, PUB_SUB_EVENTS } = window.Universe;

  class VariantSelect {
    constructor(wrap) {
      if (!wrap) {
        throw new Error('CustomSelect: wrap is required');
      }

      this.DOM = {};
      this.DOM.wrap = wrap;
      this.DOM.selectButton = this.DOM.wrap.querySelector('.js-variant-select-button');
      this.DOM.selectedValue = this.DOM.wrap.querySelector('.js-variant-select-selected-value');
      this.DOM.optionsWrap = this.DOM.wrap.querySelector('.js-variant-select-options');
      this.DOM.options = this.DOM.wrap.querySelectorAll('.js-variant-select-option');

      this.focusedIndex = -1;
      this.isOpen = false;

      this.setup();
    }

    setup() {
      if (this.DOM.optionsWrap && !this.DOM.optionsWrap.id) {
        this.DOM.optionsWrap.id = 'variant-options-' + Math.random().toString(36).slice(2, 8);
      }
      if (this.DOM.selectButton && this.DOM.optionsWrap) {
        this.DOM.selectButton.setAttribute('aria-controls', this.DOM.optionsWrap.id);
        this.DOM.selectButton.setAttribute('aria-expanded', 'false');
        this.DOM.selectButton.setAttribute('aria-haspopup', 'listbox');
      }
      if (this.DOM.optionsWrap) {
        this.DOM.optionsWrap.setAttribute('role', 'listbox');
        this.DOM.optionsWrap.setAttribute('aria-hidden', 'true');
      }

      this._onOptionClick = (event) => { this.onOptionClick(event); };
      this._onOptionKeyDown = (event) => { this.onOptionKeyDown(event); };

      for (let index = 0; index < this.DOM.options.length; index++) {
        const option = this.DOM.options[index];
        option.dataset.index = index;
        option.setAttribute('tabindex', '-1');
        if (!option.id) option.id = 'variant-option-' + index + '-' + Math.random().toString(36).slice(2, 6);
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', option.classList.contains('is-selected') ? 'true' : 'false');
        option.addEventListener('click', this._onOptionClick);
        option.addEventListener('keydown', this._onOptionKeyDown);
      }

      this._onSelectButtonClick = (event) => { this.onSelectButtonClick(event); };
      this._onSelectButtonKeyDown = (event) => { this.onSelectButtonKeyDown(event); };
      this.DOM.selectButton.addEventListener('click', this._onSelectButtonClick);
      this.DOM.selectButton.addEventListener('keydown', this._onSelectButtonKeyDown);

      this._onOutsideClick = (event) => { this.onOutsideClick(event); };
      document.addEventListener('click', this._onOutsideClick);

      this.setListInteractivity(false);

      this._onOptionsWrapKeyDown = (event) => { this.onOptionsWrapKeyDown(event); };
      this.DOM.optionsWrap.addEventListener('keydown', this._onOptionsWrapKeyDown);
    }

    toggleDropdown(expand, focusButtonOnClose = true) {
      const open = expand !== undefined ? expand : !this.isOpen;
      this.isOpen = open;

      this.DOM.optionsWrap.classList.toggle('is-active', open);
      this.DOM.selectButton.setAttribute('aria-expanded', open);
      this.setListInteractivity(open);

      if (open) {
        let selectedIndex = -1;
        for (let i = 0; i < this.DOM.options.length; i++) {
          if (this.DOM.options[i].classList.contains('is-selected')) {
            selectedIndex = i;
            break;
          }
        }
        this.focusedIndex = selectedIndex !== -1 ? selectedIndex : 0;
        this.setOptionsTabIndex('0');
        this.updateFocus();
      } else {
        this.focusedIndex = -1;
        this.setOptionsTabIndex('-1');
        if (focusButtonOnClose) {
          this.DOM.selectButton.focus();
        }
      }
    }

    updateFocus() {
      this.DOM.options.forEach((option, index) => {
        if (option) {
          option.setAttribute('tabindex', index === this.focusedIndex ? '0' : '-1');
          if (index === this.focusedIndex) option.focus();
        }
      });
    }

    setOptionsTabIndex(value) {
      this.DOM.options.forEach((option) => { option.setAttribute('tabindex', value); });
    }

    handleOptionSelect(option) {
      this.DOM.options.forEach((opt) => { opt.classList.remove('is-selected'); });
      option.classList.add('is-selected');
      this.DOM.options.forEach((opt) => { opt.setAttribute('aria-selected', 'false'); });
      option.setAttribute('aria-selected', 'true');
      this.DOM.selectedValue.textContent = option.textContent.trim();
      this.DOM.wrap.dataset.selectedValue = option.dataset.value;

      subEvents.publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          target: option,
        },
      });
    }

    onOutsideClick(event) {
      const isOutsideClick = !this.DOM.wrap.contains(event.target);

      if (this.isOpen && isOutsideClick) {
        this.toggleDropdown(false, false);
      }
    }

    onOptionClick(event) {
      this.handleOptionSelect(event.currentTarget);
      this.toggleDropdown(false);
      this.DOM.selectButton.focus();
    }

    onOptionKeyDown(event) {
      const key = event.key;
      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        this.handleOptionSelect(event.currentTarget);
        this.toggleDropdown(false);
        this.DOM.selectButton.focus();
      } else if (key === 'ArrowDown' || key === 'Down') {
        event.preventDefault();
        this.focusMove(1);
      } else if (key === 'ArrowUp' || key === 'Up') {
        event.preventDefault();
        this.focusMove(-1);
      } else if (key === 'Escape' || key === 'Esc') {
        event.preventDefault();
        this.toggleDropdown(false);
        this.DOM.selectButton.focus();
      }
    }

    focusMove(delta) {
      if (!this.DOM.options.length) return;
      const next = Math.min(Math.max(this.focusedIndex + delta, 0), this.DOM.options.length - 1);
      this.focusedIndex = next;
      this.updateFocus();
    }

    onSelectButtonClick(event) {
      event.preventDefault();
      this.toggleDropdown();
    }

    onSelectButtonKeyDown(event) {
      const key = event.key;
      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        this.toggleDropdown();
        if (this.isOpen) this.updateFocus();
      } else if (key === 'ArrowDown' || key === 'Down') {
        event.preventDefault();
        this.toggleDropdown(true, false);
        this.focusMove(1);
      } else if (key === 'ArrowUp' || key === 'Up') {
        event.preventDefault();
        this.toggleDropdown(true, false);
        this.focusMove(-1);
      } else if (key === 'Escape' || key === 'Esc') {
        if (this.isOpen) {
          event.preventDefault();
          this.toggleDropdown(false);
        }
      } else if (key === 'Tab' && this.isOpen) {
        event.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex, 0);
        this.updateFocus();
      }
    }

    getSelectedValue() {
      const selectedValueEl = this.DOM.wrap.querySelector('.js-variant-select-option.is-selected');
      return selectedValueEl
        ? {
            id: selectedValueEl.dataset.optionValueId,
            value: selectedValueEl.dataset.value,
          }
        : null;
    }

    destroy() {
      if (this.DOM) {
        for (let index = 0; index < this.DOM.options.length; index++) {
          this.DOM.options[index].removeEventListener('click', this._onOptionClick);
          this.DOM.options[index].removeEventListener('keydown', this._onOptionKeyDown);
        }

        this.DOM.selectButton.removeEventListener('click', this._onSelectButtonClick);
        this.DOM.selectButton.removeEventListener('keydown', this._onSelectButtonKeyDown);
        document.removeEventListener('click', this._onOutsideClick);
        this.DOM.optionsWrap.removeEventListener('keydown', this._onOptionsWrapKeyDown);

        this.DOM = undefined;
      }
    }

    setListInteractivity(enable) {
      if (!this.DOM || !this.DOM.optionsWrap) return;

      const supportsInert = 'inert' in HTMLElement.prototype;
      this.DOM.optionsWrap.setAttribute('aria-hidden', enable ? 'false' : 'true');

      if (supportsInert) {
        this.DOM.optionsWrap.inert = !enable;
      } else {
        const focusables = this.DOM.optionsWrap.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        focusables.forEach((el) => {
          if (enable) {
            if (el.hasAttribute('data-prev-tabindex')) {
              const prev = el.getAttribute('data-prev-tabindex');
              if (prev === '') el.removeAttribute('tabindex');
              else el.setAttribute('tabindex', prev);
              el.removeAttribute('data-prev-tabindex');
            } else if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
          } else {
            if (!el.hasAttribute('data-prev-tabindex')) {
              el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex') || '');
            }
            el.setAttribute('tabindex', '-1');
          }
        });
      }
    }

    onOptionsWrapKeyDown(event) {
      if (!this.isOpen) return;
      if (event.key !== 'Tab') return;

      const options = Array.from(this.DOM.options);
      if (!options.length) return;

      event.preventDefault();

      const activeIndex = options.findIndex((opt) => opt === document.activeElement);
      let nextIndex = activeIndex;

      if (event.shiftKey) {
        nextIndex = activeIndex <= 0 ? options.length - 1 : activeIndex - 1;
      } else {
        nextIndex = activeIndex >= options.length - 1 ? 0 : activeIndex + 1;
      }

      this.focusedIndex = nextIndex;
      this.updateFocus();
    }
  }

  window.Universe = window.Universe || {};
  window.Universe.VariantSelect = VariantSelect;
})();
